// src/components/enhanced-table/index.ts
export type Order = 'asc' | 'desc';

export interface ColumnVisibility {
  [key: string]: boolean;
}

export interface TableState {
  order: Order;
  orderBy: string;
  selected: string[];
  page: number;
  rowsPerPage: number;
  searchKey: string;
  filterValues: Record<string, any>;
  columnVisibility: ColumnVisibility;
  globalFilter: string;
}

// Enhanced sorting with null/undefined handling
export function createSortComparator<T>(
  order: Order,
  orderBy: keyof T,
  customComparators?: Record<string, (a: any, b: any) => number>
): (a: T, b: T) => number {
  const customComparator = customComparators?.[orderBy as string];
  
  return (a: T, b: T) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];
    
    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Use custom comparator if provided
    if (customComparator) {
      const result = customComparator(aValue, bValue);
      return order === 'desc' ? -result : result;
    }
    
    // Default comparison
    let result = 0;
    if (aValue < bValue) result = -1;
    if (aValue > bValue) result = 1;
    
    return order === 'desc' ? -result : result;
  };
}

export interface ITableMenuAction {
  key: string;
  title: string;
  icon?: React.ReactNode;
  onClick: (selected: string[]) => void | Promise<void>;
  disabled?: (selected: string[]) => boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  confirmDialog?: {
    title: string;
    message: string | ((count: number) => string);
  };
  visible?: (selected: string[]) => boolean;
}

export enum EnhancedTableColumnType {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  DATETIME = 'datetime',
  BOOLEAN = 'boolean',
  COLORED_CHIP = 'colored_chip',
  LINK = 'link',
  CLICKABLE = 'clickable',
  BARCODE = 'barcode', 
  CURRENCY = 'currency',
  ACTION = 'action',
  PHONE_NUMBER = 'phone_number',
  EMAIL = 'email',
  PROGRESS = 'progress',
  RATING = 'rating',
  CUSTOM = 'custom',
  IMAGE = 'image',
  TAGS = 'tags',
  STATUS_BADGE = 'status_badge',
  AVATAR = 'avatar',
  AVATAR_GROUP = 'avatar_group',
  COLOR = 'color',
}

// Base column interface
export interface IEnhancedTableColumn {
  id: string;
  label: string;
  type: EnhancedTableColumnType;
  numeric?: boolean;
  disablePadding?: boolean;
  sortable?: boolean;
  hideable?: boolean;
  sticky?: boolean;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  align?: 'left' | 'center' | 'right' | 'justify';
  tooltip?: string | ((row: any) => string);
  exportable?: boolean;
  searchable?: boolean;
  className?: string;
  headerClassName?: string;
  cellClassName?: string | ((row: any) => string);
  editable?: boolean;
  required?: boolean;
}

export interface IBarcodeColumn extends IEnhancedTableColumn {
  type: EnhancedTableColumnType.BARCODE;
  barcodeConfig?: {
    format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'CODE93' | 'ITF14' | 'MSI' | 'pharmacode';
    width?: number;
    height?: number;
    displayValue?: boolean;
    fontSize?: number;
    textMargin?: number;
    margin?: number;
  };
}

export interface IColorColumn extends IEnhancedTableColumn {
  type: EnhancedTableColumnType.COLOR;
  shape?: 'square' | 'circle';
  showLabel?: boolean;
}

export interface IColoredChipColumn extends IEnhancedTableColumn {
  type: EnhancedTableColumnType.COLORED_CHIP;
  chipColors?: Record<string, {
    color: string;
    backgroundColor: string;
    icon?: React.ReactNode;
  }>;
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
}

export interface IActionColumn extends IEnhancedTableColumn {
  type: EnhancedTableColumnType.ACTION;
  actions: Array<{
    key?: string;
    icon: React.ReactNode;
    label: string;
    title?: string;
    onClick: (id: string, row: any) => void | Promise<void>;
    hidden?: (row: any) => boolean;
    disabled?: (row: any) => boolean;
    visible?: (row: any) => boolean;
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    confirmDialog?: {
      title: string;
      message: string | ((row: any) => string);
    };
  }>;
  actionsPosition?: 'left' | 'right' | 'center';
  moreActionsLabel?: string;
  maxVisibleActions?: number;
}

export interface IProgressColumn extends IEnhancedTableColumn {
  type: EnhancedTableColumnType.PROGRESS;
  min?: number;
  max?: number;
  showPercentage?: boolean;
  showValue?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | ((value: number) => string);
  variant?: 'linear' | 'circular';
  size?: 'small' | 'medium' | 'large';
  expectedField?: string;
  actualField?: string;
  dueDateField?: string;
}

export interface EnhancedTableColumn {
  id: string;
  label: string;
  type: EnhancedTableColumnType;
  numeric?: boolean;
  disablePadding?: boolean;
  sortable?: boolean;
  hideable?: boolean;
  sticky?: boolean;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  align?: 'left' | 'center' | 'right' | 'justify';
  tooltip?: string | ((row: any) => string);
  exportable?: boolean;
  searchable?: boolean;
  className?: string;
  headerClassName?: string;
  cellClassName?: string | ((row: any) => string);
  editable?: boolean;
  required?: boolean;
  
  chipColors?: Record<string, { backgroundColor: string; color: string }>;
  
  render?: (value: any, row: any, key: string) => React.ReactNode;
  
  barcodeConfig?: {
    format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'CODE93' | 'ITF14' | 'MSI' | 'pharmacode';
    width?: number;
    height?: number;
    displayValue?: boolean;
    fontSize?: number;
    textMargin?: number;
    margin?: number;
  };
  
  actions?: Array<{
    key?: string;
    icon: React.ReactNode;
    label: string;
    title?: string;
    onClick: (id: string, row: any) => void | Promise<void>;
    hidden?: (row: any) => boolean;
    disabled?: (row: any) => boolean;
    visible?: (row: any) => boolean;
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  }>;
  
  expectedField?: string;
  actualField?: string;
  dueDateField?: string;
  
  href?: string | ((row: any) => string);
  onClick?: (rowId: string, row: any) => void;
  
  [key: string]: any;
}

export interface ExportOptions {
  filename?: string;
  format?: 'csv' | 'xlsx' | 'json';
  columns?: string[];
  includeHeaders?: boolean;
  dateFormat?: string;
  numberFormat?: string;
}

export interface SelectionOptions {
  showCheckbox?: boolean;
  selectOnRowClick?: boolean;
  selectionMode?: 'single' | 'multiple';
  disableSelection?: (row: any) => boolean;
  onSelectionChange?: (selected: string[]) => void;
}

export interface VirtualizationOptions {
  enabled?: boolean;
  rowHeight?: number | ((row: any) => number);
  overscan?: number;
}

export interface EnhancedTableProps {
  columns: EnhancedTableColumn[];
  data: Record<string, any>;
  idField?: string;
  
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  
  actions?: ITableMenuAction[];
  filters?: ITableFilterType[];
  defaultState?: Partial<TableState>;
  
  selection?: SelectionOptions;
  
  dense?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string | number;
  striped?: boolean;
  bordered?: boolean;
  hoverable?: boolean;
  
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
  boxedCells?: boolean;
  boxedCellClass?: string;
  stripedColors?: string[] | ((theme: any) => string[]);
  
  actionsChild?: React.ReactNode;
  rowActionsOutsideMenu?: boolean;
  onAddRow?: (row: any) => Promise<any>;
  onEditRow?: (row: any) => Promise<any>;
  addRowFields?: string[];
  showEditOnTable?: boolean;
  collapsible?: boolean;
  
  virtualization?: VirtualizationOptions;
  export?: ExportOptions | boolean;
  print?: boolean;
  columnChooser?: boolean;
  fullscreen?: boolean;
  
  onRowClick?: (id: string, row: any) => void;
  onRowDoubleClick?: (id: string, row: any) => void;
  onStateChange?: (state: TableState) => void;
  
  components?: {
    Toolbar?: React.ComponentType<any>;
    Row?: React.ComponentType<any>;
    Cell?: React.ComponentType<any>;
    EmptyState?: React.ComponentType<any>;
    LoadingState?: React.ComponentType<any>;
    ErrorState?: React.ComponentType<any>;
  };
  
  sx?: any;
  className?: string;
}

export enum TableFilterType {
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  DATERANGE = 'daterange',
  BOOLEAN = 'boolean',
  CUSTOM = 'custom',
  COLOR = 'color',
}

export interface ITableFilterBase {
  id: string;
  label: string;
  type: TableFilterType;
  column: string;
  placeholder?: string;
  helperText?: string;
  defaultValue?: any;
  clearable?: boolean;
  visible?: boolean;
  width?: string | number;
}

export interface ISelectFilter extends ITableFilterBase {
  type: TableFilterType.SELECT | TableFilterType.MULTISELECT;
  options: Array<{ value: any; label: string }> | ((data: any[]) => Array<{ value: any; label: string }>);
  multiple?: boolean;
}

export interface ITextFilter extends ITableFilterBase {
  type: TableFilterType.TEXT;
  debounceMs?: number;
  matchMode?: 'contains' | 'startsWith' | 'endsWith' | 'equals';
}

export interface IColorFilter extends ITableFilterBase {
  type: TableFilterType.COLOR;
}

export interface INumberFilter extends ITableFilterBase {
  type: TableFilterType.NUMBER;
  min?: number;
  max?: number;
  step?: number;
  operator?: 'equals' | 'notEquals' | 'greaterThan' | 'lessThan' | 'between';
}

export interface IDateFilter extends ITableFilterBase {
  type: TableFilterType.DATE;
  minDate?: Date;
  maxDate?: Date;
  operator?: 'equals' | 'before' | 'after' | 'between';
}

export interface IDateRangeFilter extends ITableFilterBase {
  type: TableFilterType.DATERANGE;
  minDate?: Date;
  maxDate?: Date;
}

export interface IBooleanFilter extends ITableFilterBase {
  type: TableFilterType.BOOLEAN;
  trueLabel?: string;
  falseLabel?: string;
}

export interface ICustomFilter extends ITableFilterBase {
  type: TableFilterType.CUSTOM;
  component: React.ComponentType<{
    value: any;
    onChange: (value: any) => void;
    data: any[];
  }>;
  filterFn: (row: any, value: any) => boolean;
}

export type ITableFilterType = 
  | ISelectFilter 
  | ITextFilter 
  | INumberFilter 
  | IDateFilter 
  | IDateRangeFilter 
  | IBooleanFilter 
  | IColorFilter 
  | ICustomFilter;